import { Event, SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import {
  GetCardAction,
  MakePaymentManualOrderAction,
  MakePaymentAction,
  GetTransactionDetailsAction,
  AddCardAction,
  DeleteCardAction,
  GetCardsAction,
  UpdateCardAction,
} from 'sdk/composable-commerce/types/actions/AuthorizeNetActions';
import {
  DeleteCardPayload,
  SetDefaultCreditCardPayload,
  AddCardPayload,
  UpdateCardPayload,
  GetCardPayload,
  GetTransactionDetailsPayload,
  MakePaymentPayload,
  MakePaymentManualOrderPayload,
} from 'sdk/composable-commerce/types/payloads/AuthorizeNetPayloads';
import { GetTransactionDetailsResponse } from 'shared/types/account/AuthorizeNetAccount';

export type AuthorizeNetActions = {
  getCards: GetCardsAction;
  deleteCard: DeleteCardAction;
  addCard: AddCardAction;
  updateCard: UpdateCardAction;
  getCard: GetCardAction;
  getTransactionDetails: GetTransactionDetailsAction;
  makePayment: MakePaymentAction;
  makePaymentManualOrder: MakePaymentManualOrderAction;
};

export const getAuthorizeNetActions = (sdk: SDK<ComposableCommerceEvents>): AuthorizeNetActions => {
  return {
    getCards: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<unknown>({
        actionName: 'authorizeNet/getCards',
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetCardsFetched',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
    deleteCard: async (
      payload: DeleteCardPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<unknown>({
        actionName: 'authorizeNet/deleteCard',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetCardDeleted',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
    addCard: async (
      payload: AddCardPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<unknown>({
        actionName: 'authorizeNet/addCard',
        payload: {
          ...payload,
        },
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetCardAdded',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
    updateCard: async (
      payload: UpdateCardPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<unknown>({
        actionName: 'authorizeNet/updateCard',
        payload: {
          ...payload,
        },
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetCardUpdated',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
    getCard: async (
      payload: GetCardPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<unknown>({
        actionName: 'authorizeNet/getCard',
        payload: {
          ...payload,
        },
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetCardFetched',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
    getTransactionDetails: async (
      payload: GetTransactionDetailsPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<GetTransactionDetailsResponse>({
        actionName: 'authorizeNet/getTransactionDetails',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetTransactionDetailsFetched',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
    makePayment: async (
      payload: MakePaymentPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<unknown>({
        actionName: 'authorizeNet/payment',
        payload: {
          ...payload,
        },
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetPaymentMade',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
    makePaymentManualOrder: async (
      payload: MakePaymentManualOrderPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<unknown>({
        actionName: 'authorizeNet/makePaymentManualOrder',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'authorizeNetPaymentManualOrderMade',
            data: {
              data: response.data,
            },
          }),
        );
      }
      return response;
    },
  };
};
